import { z } from 'zod';
export const HttpAuthTypeSchema = z.enum(['none', 'bearer_static', 'basic', 'login_form']);
export const HttpMethodSchema = z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']);
/**
 * login_form 흐름 정의.
 * 1. method + path 로 bodyTemplate 을 JSON POST.
 *    bodyTemplate 안의 `{{username}}` / `{{password}}` 는 endpoint 에 저장된 loginUsername/loginPassword 로 치환.
 * 2. 응답 JSON 에서 tokenJsonPath 에 해당하는 필드를 토큰으로 추출 (예: 'accessToken').
 * 3. 후속 요청마다 `${tokenHeaderName}: ${tokenPrefix}${token}` 을 Authorization 헤더로 주입.
 * sessionTtlSec 동안 메모리 캐시 유지.
 *
 * 이 스키마는 응답/저장 시점의 "완전한" 형태 — 모든 필드는 필수.
 * 요청 시 partial 입력은 LoginConfigInputSchema 사용.
 */
export const LoginConfigSchema = z.object({
    method: HttpMethodSchema,
    path: z.string().min(1).max(500),
    bodyTemplate: z.record(z.string(), z.unknown()),
    tokenJsonPath: z.string().min(1).max(200),
    tokenHeaderName: z.string().min(1).max(100),
    tokenPrefix: z.string().max(20),
    sessionTtlSec: z.number().int().min(30).max(86400),
});
export const DEFAULT_LOGIN_CONFIG = {
    method: 'POST',
    path: '/login',
    bodyTemplate: {},
    tokenJsonPath: 'accessToken',
    tokenHeaderName: 'Authorization',
    tokenPrefix: 'Bearer ',
    sessionTtlSec: 1800,
};
/** Create/Update 요청 body 용 — partial 입력을 허용, server 에서 DEFAULT_LOGIN_CONFIG 와 merge. */
export const LoginConfigInputSchema = z
    .object({
    method: HttpMethodSchema.optional(),
    path: z.string().min(1).max(500).optional(),
    bodyTemplate: z.record(z.string(), z.unknown()).optional(),
    tokenJsonPath: z.string().min(1).max(200).optional(),
    tokenHeaderName: z.string().min(1).max(100).optional(),
    tokenPrefix: z.string().max(20).optional(),
    sessionTtlSec: z.number().int().min(30).max(86400).optional(),
})
    .transform((v) => ({ ...DEFAULT_LOGIN_CONFIG, ...v }));
/** 응답 스키마 — 암호화 필드는 제외. */
export const HttpEndpointSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    baseUrl: z.string(),
    description: z.string().nullable(),
    authType: HttpAuthTypeSchema,
    basicUsername: z.string().nullable(),
    loginUsername: z.string().nullable(),
    loginConfig: LoginConfigSchema,
    cookieMode: z.boolean(),
    allowedMethods: z.array(HttpMethodSchema),
    pathAllowPatterns: z.array(z.string()),
    pathDenyPatterns: z.array(z.string()),
    defaultHeaders: z.record(z.string(), z.string()),
    requestTimeoutMs: z.number().int(),
    maxResponseBytes: z.number().int(),
    hasBearerToken: z.boolean(),
    hasBasicPassword: z.boolean(),
    hasLoginPassword: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const HttpEndpointListSchema = z.object({
    endpoints: z.array(HttpEndpointSchema),
});
const baseFields = {
    name: z.string().min(1).max(80),
    baseUrl: z
        .string()
        .url()
        .refine((v) => v.startsWith('http://') || v.startsWith('https://'), {
        message: 'baseUrl 은 http(s) 여야 합니다.',
    })
        .refine((v) => !v.endsWith('/'), {
        message: 'baseUrl 끝의 슬래시는 제거해주세요.',
    }),
    description: z.string().max(2000).nullable().default(null),
    authType: HttpAuthTypeSchema.default('none'),
    /** authType=bearer_static. 저장 시 sealSecret. 빈 문자열이면 기존 값 유지(수정) 또는 무시(생성). */
    bearerToken: z.string().max(4000).optional(),
    /** authType=basic */
    basicUsername: z.string().max(200).nullable().default(null),
    basicPassword: z.string().max(500).optional(),
    /** authType=login_form */
    loginUsername: z.string().max(200).nullable().default(null),
    loginPassword: z.string().max(500).optional(),
    loginConfig: LoginConfigInputSchema.default({}),
    cookieMode: z.boolean().default(false),
    allowedMethods: z.array(HttpMethodSchema).min(1).max(5).default(['GET']),
    pathAllowPatterns: z.array(z.string().min(1).max(500)).max(50).default([]),
    pathDenyPatterns: z.array(z.string().min(1).max(500)).max(50).default([]),
    defaultHeaders: z.record(z.string().min(1).max(100), z.string().max(2000)).default({}),
    requestTimeoutMs: z.number().int().min(500).max(60000).default(10000),
    maxResponseBytes: z.number().int().min(1024).max(1048576).default(65536),
};
export const CreateHttpEndpointRequestSchema = z.object(baseFields);
export const UpdateHttpEndpointRequestSchema = z.object({
    name: baseFields.name.optional(),
    baseUrl: baseFields.baseUrl.optional(),
    description: baseFields.description.optional(),
    authType: baseFields.authType.optional(),
    bearerToken: baseFields.bearerToken,
    basicUsername: baseFields.basicUsername.optional(),
    basicPassword: baseFields.basicPassword,
    loginUsername: baseFields.loginUsername.optional(),
    loginPassword: baseFields.loginPassword,
    loginConfig: baseFields.loginConfig.optional(),
    cookieMode: baseFields.cookieMode.optional(),
    allowedMethods: baseFields.allowedMethods.optional(),
    pathAllowPatterns: baseFields.pathAllowPatterns.optional(),
    pathDenyPatterns: baseFields.pathDenyPatterns.optional(),
    defaultHeaders: baseFields.defaultHeaders.optional(),
    requestTimeoutMs: baseFields.requestTimeoutMs.optional(),
    maxResponseBytes: baseFields.maxResponseBytes.optional(),
});
export const TestHttpEndpointResponseSchema = z.object({
    ok: z.boolean(),
    /** none/bearer/basic 타입에서는 probe GET (baseUrl/) 결과 status. login_form 은 로그인 응답 status. */
    status: z.number().int().nullable(),
    error: z.string().nullable(),
    tokenPreview: z.string().nullable(),
    latencyMs: z.number().int().nonnegative(),
});
//# sourceMappingURL=http-endpoints.js.map